import React from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { rematchConcept } from '../api';
import ClassTargetPanel from '../ClassTargetPanel';

// Stage 3: does an existing class already cover this, and where does the content go.
//
// These two belong together: the match verdict is what JPRO derives the class target from,
// so reading the verdict and agreeing to the target is one decision, not two screens.
const matchTone = (m) =>
  m === 'strong' ? 'bg-[#0b2a17] text-green-400 border-[#1c4a30]'
  : m === 'partial' ? 'bg-[#241a0b] text-[#f7a23b] border-[#6b4516]'
  : m === 'no' ? 'bg-[#2a1414] text-[#ffb4b4] border-[#5c2b2b]'
  : 'bg-[#1a1f28] text-[#8a95a8] border-[#2b3a4d]';

const EXPLAIN = {
  strong: 'An existing class already covers this ground, so this should revise that class rather than duplicate it.',
  partial: 'An existing class overlaps but misses part of this, so this is a revision that extends it.',
  no: 'Nothing in the catalog covers this, so it becomes a new class.',
  unclear: 'JPRO could not tell whether an existing class covers this. It defaults to a new class rather than risk revising the wrong one.',
};

export default function MatchStage({ concept: c, conceptId, onChanged, locked }) {
  const rematch = useMutation({
    mutationFn: () => rematchConcept(conceptId),
    onSuccess: (res) => {
      toast.success(`Re-matched: ${res?.match?.result || 'done'}`);
      onChanged();
    },
    onError: (e) => toast.error(e?.message || 'Could not re-match this concept'),
  });

  const result = c.existing_zu_match_result || 'unclear';

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0]">Existing-content match</div>
          {/* Re-running the match re-derives the class target, so it stays available while
              the concept is editable. It leaves a confirmed target alone. */}
          {!locked && (
            <button onClick={() => rematch.mutate()} disabled={rematch.isPending}
              className="text-[11px] text-[#5ea9ff] disabled:opacity-40">
              {rematch.isPending ? 'Re-matching…' : 'Re-match the catalog'}
            </button>
          )}
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded border ${matchTone(result)}`}>
          {String(result).toUpperCase()}
        </span>
        <div className="text-[11px] text-[#8a95a8] mt-2">{EXPLAIN[result]}</div>

        {(c.matched_content || []).length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0] mb-1">Matched classes</div>
            {(c.matched_content || []).map((m, i) => (
              <div key={i} className="text-xs text-[#c7d0e0] mb-1.5">
                <div>{m.title}</div>
                {m.reason && <div className="text-[10px] text-[#7d8aa0]">{m.reason}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-[#1d2634] pt-4">
        <ClassTargetPanel concept={c} conceptId={conceptId} onChanged={onChanged} />
      </div>
    </div>
  );
}
